import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Row, Col, Button, Badge, Spinner, Alert } from 'react-bootstrap';
import Layout from '../components/Layout';
import FormPageTitle from '../components/FormPageTitle';
import Pagination from '../components/Pagination';
import { promocodeTemplateService, PromocodeTemplate } from '../services/promocodeTemplateService';
import { authService, User } from '../services/authService';
import { API_BASE_URL, STORAGE_PATHS, ROUTES } from '../constants';
import { useTranslation } from '../contexts/LocaleContext';

const PAGE_SIZE = 6;

const PromocodeTemplates: React.FC = () => {
  const t = useTranslation();
  const { id: categoryId } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<PromocodeTemplate[]>([]);
  const [user, setUser] = useState<User | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadUser();
  }, []);

  useEffect(() => {
    loadTemplates(currentPage, categoryId || null);
  }, [categoryId, currentPage]);

  const loadUser = async () => {
    const currentUser = await authService.getCurrentUser();
    setUser(currentUser);
  };


  const loadTemplates = async (page: number = 1, categoryId: string | null) => {
    setLoading(true);
    setError(null);
    try {
      const response = await promocodeTemplateService.getTemplates(categoryId, page, PAGE_SIZE);
      setTemplates(response.data);
      setCurrentPage(page);
      setLastPage(response.last_page);
      setTotalCount(response.total);
    } catch (err) {
      console.error('Error loading templates:', err);
      setError(t('promocode.notFound') || t('errors.loadTemplatesFailed'));
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(t('manage.delete') + '?')) return;
    try {
      await promocodeTemplateService.deleteTemplate(id);
      await loadTemplates(currentPage, categoryId || null);
    } catch (err) {
      console.error('Error deleting template:', err);
      alert(t('errors.deleteTemplate'));
    }
  };

  const isSuperAdmin = user?.roles?.includes('Admin') || false;
  const canEdit = user?.permissions?.includes('promocode-templates.update') || isSuperAdmin;
  const canDelete = user?.permissions?.includes('promocode-templates.delete') || isSuperAdmin;
  const canCreate = user?.permissions?.includes('promocode-templates.create') || isSuperAdmin;

  const imageSrc = (path: string) =>{
    return path?
    path.startsWith('http') ? path : `${API_BASE_URL}${path}` : ""}

  return (
    <Layout>
      {categoryId && (
        <Row className="my-2">
          <Col xs={12}>
            <Button
              variant="link"
              className="text-secondary text-decoration-none p-0 align-baseline"
              onClick={() => {
                if (window.history.length > 1) {
                  navigate(-1);
                } else {
                  navigate(ROUTES.HOME);
                }
              }}
            >
              ← {t('manage.back')}
            </Button>
          </Col>
        </Row>
      )}
      <FormPageTitle title={t('templates')} helperContent={!categoryId ? t('helpers.promocodeTemplates.title') : undefined} />

      {canCreate && (
        <Row className="my-2 d-flex ">
          <Col className='p-0'>
            <Button
              variant="primary"
              onClick={() => navigate(ROUTES.PROMOCODE_TEMPLATE_CREATE)}
            >
              {t('promocode.create')}
            </Button>
          </Col>
        </Row>
      )}


      {loading && (
        <div className="d-flex justify-content-center my-4">
          <Spinner animation="border" role="status" />
        </div>
      )}

      {!loading && templates.length === 0 && (
        <div className="text-secondary text-center my-2">{t('promocode.notFound')}</div>
      )}

      {!loading && (
        <div className="mx-auto">
          <Row className="g-2">
            {templates.map((template) => (
              <Col key={template.id} xs={12} xl={4} lg={6}>
              <div className="p-2 text-bg-light border rounded h-100 d-flex flex-column">
                <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
                  <div className="d-flex gap-2 py-1 flex-wrap">
                    <Badge bg="primary" className="">
                      {template.name}
                    </Badge>
                    <Badge bg="success" className="">
                      {template.type === 'image' ? t('promocode.image') : t('promocode.video')}
                    </Badge>
                    {template.data && (!template.data.x || !template.data.y || template.data.x === 0 || template.data.y === 0) && (
                      <Badge bg="danger" className="">
                        {t('promocode.waitingForSetup')}
                      </Badge>
                    )}
                    {template.user && (
                      <Badge bg="secondary" className="">
                        {t('createdBy')} {template.user.name}
                      </Badge>
                    )}
                  </div>
                  <div className="d-flex flex-row flex-wrap gap-2">
                    {canEdit && (
                      <Button
                        variant="success"
                        className=""
                        onClick={() =>
                          navigate(ROUTES.PROMOCODE_TEMPLATE_EDIT(template.id))
                        }
                      >
                        {t('manage.edit')}
                      </Button>
                    )}
                    {canDelete && (
                      <Button
                        variant="danger"
                        className=""
                        onClick={() => handleDelete(template.id)}
                      >
                        {t('manage.delete')}
                      </Button>
                    )}
                    {template.category && (
                      <Button
                        variant="primary"
                        className=""
                        onClick={() => navigate(ROUTES.CATEGORY_PROMOCODE_TEMPLATES(template.category!.id))}
                      >
                        <span>
                          {t('categoryName')}: {template.category.name}
                        </span>
                      </Button>
                    )}
                  </div>
                </div>
                <div className="d-flex justify-content-center align-items-center pt-2"
                style={{flex:'1 1 auto'}}
                >
                  {template.type === 'video' && template.file && (() => {
                    const src = template.file.url ?? template.file.path ?? '';
                    return src ? (
                      <video
                        className="img-fluid template-preview-media"
                        src={src.startsWith('http') ? src : imageSrc(src)}
                        muted
                        loop
                        playsInline
                        autoPlay
                      />
                    ) : null;
                  })()}
                  {template.type === 'image' && template.file && (() => {
                    const src = template.file.url ?? template.file.path ?? '';
                    return src ? (
                      <img
                        className="img-fluid template-preview-media"
                        src={src.startsWith('http') ? src : imageSrc(src)}
                        alt=""
                      />
                    ) : null;
                  })()}
                </div>
              </div>
              </Col>
            ))}
          </Row>
        </div>
      )}
      {!loading && lastPage > 1 && (
        <Pagination
          currentPage={currentPage}
          lastPage={lastPage}
          totalCount={totalCount}
          pageSize={PAGE_SIZE}
          onPageChange={(page) => setCurrentPage(page)}
        />
      )}
    </Layout>
  );
};

export default PromocodeTemplates;
